import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { FileText, Globe, Share2, Mail, Image, Video, LogOut, Sparkles, Search, User, Calendar, Link as LinkIcon, PenTool, ArrowRight } from 'lucide-react';
import { API } from '../App';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [profileComplete, setProfileComplete] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    checkProfileStatus();
  }, [user, navigate]);

  const checkProfileStatus = async () => {
    try {
      const response = await fetch(`${API}/profile/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const profile = await response.json();
        // Check if essential fields are filled (using new list-based fields)
        const isComplete = profile && 
          profile.business_name && 
          profile.industry && 
          profile.city &&
          profile.services_list && profile.services_list.length > 0;
        
        setProfileComplete(isComplete);
        
        // DON'T auto-redirect - let users access dashboard even with incomplete profile
        // They can complete their profile when ready
      } else {
        // No profile found - still allow dashboard access
        setProfileComplete(false);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      setCheckingProfile(false);
    }
  };

  if (!user || checkingProfile) return null;

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <Helmet>
        <title>Dashboard | QuillFire - Your Content Command Center</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src="/quillfire-logo.png" alt="QuillFire" className="w-10 h-10 rounded-lg" />
            <span className="text-2xl font-bold text-gray-900">QuillFire</span>
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-500 capitalize">{user.plan} Plan</p>
            </div>
            {/* Admin Link - ONLY for michael@studiosites.com */}
            {user.email === 'michael@studiosites.com' && (
              <Button 
                onClick={() => navigate('/admin')} 
                variant="outline"
                size="sm"
                className="flex border-red-300 text-red-600 hover:bg-red-50"
              >
                <span>🔐 Admin</span>
              </Button>
            )}
            <Button 
              onClick={() => navigate('/profile')} 
              variant="outline"
              size="sm"
              className="flex"
            >
              <User className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Profile</span>
            </Button>
            <Button onClick={onLogout} variant="outline" size="sm" data-testid="dashboard-logout-btn">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-3 text-gray-900" data-testid="dashboard-welcome">Welcome back, {user.name?.split(' ')[0] || user.name}!</h1>
            <p className="text-xl text-gray-600">Choose a module to start building your content visibility.</p>
          </div>
          <Button 
            onClick={() => navigate('/keyword-research')}
            className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-3 whitespace-nowrap"
          >
            Start Here: Discover Questions →
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-600 mb-1">Posts Generated</p>
            <p className="text-3xl font-bold text-gray-900">{user.campaigns_count || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-600 mb-1">This Month&apos;s Usage</p>
            <p className="text-3xl font-bold text-gray-900">
              {user.monthly_blogs_used || 0} / {user.plan === 'free' ? '5' : user.plan === 'starter' ? '30' : '90'} posts
            </p>
            <p className="text-xs text-gray-500 mt-1">Resets on billing date</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-600 mb-1">Current Plan</p>
            <p className="text-3xl font-bold text-orange-600 capitalize">{user.plan}</p>
            <button 
              onClick={() => window.location.href = '/#pricing'}
              className="text-xs text-orange-600 hover:text-orange-700 underline mt-1"
            >
              View pricing & limits →
            </button>
          </div>
        </div>

        {/* Hero Feature - Question Discovery */}
        <div className="mb-12">
          <div className="mb-6">
            <div className="inline-block bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
              🔥 START HERE - STEP 1
            </div>
            <h2 className="text-3xl font-bold mb-2 text-gray-900">Discover the Questions Customers Ask (in Google + AI Search)</h2>
            <p className="text-lg text-gray-600">
              Get 150+ real questions people ask in your market - plus search volume. Then turn any question into a publish-ready answer post built to get found in Google and AI recommendations.
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => navigate('/keyword-research')}
              className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-2xl p-8 hover:shadow-2xl hover:scale-[1.02] transition-all group w-full md:w-auto inline-flex items-center justify-between md:justify-start gap-6"
              data-testid="keyword-research-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Search className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-1">Question Discovery</h3>
                  <p className="text-orange-100 text-sm">Find real customer questions in seconds (with search volume)</p>
                </div>
              </div>
              <div className="flex items-center text-white font-semibold group-hover:translate-x-2 transition-transform">
                Discover Questions →
              </div>
            </button>
            <p className="text-sm text-gray-500 italic pl-2">
              💡 Tip: Run Question Discovery once, then generate multiple posts from the results. Each run counts toward your plan limit.
            </p>
          </div>
        </div>

        <div className="mb-6">
          <div className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
            STEP 2 - CREATE CONTENT
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Answer Engine</h2>
          <p className="text-gray-600">Create 1,600–2,300 word answer posts that match real customer questions - complete with SEO structure, FAQs, internal links, and a CTA tailored to your business.</p>
        </div>

        {/* Step 2 Module - Answer Post Generator (matching Step 1 style) */}
        <div className="mb-12">
          <div className="space-y-3">
            <button
              onClick={() => navigate('/keyword-research')}
              className="relative rounded-2xl p-8 w-full text-left group transition-all duration-300 border-l-4 bg-gradient-to-r from-orange-50 to-orange-100/50 border-orange-500 hover:shadow-2xl hover:scale-[1.01] hover:border-orange-600 cursor-pointer"
              data-testid="answer-post-generator-card"
            >
              {/* Step 2 chip */}
              <div className="absolute top-4 left-4">
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-orange-500 text-white">
                  STEP 2
                </span>
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mt-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 bg-orange-500/20">
                    <PenTool className="w-8 h-8 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1 transition-colors text-gray-900 group-hover:text-orange-600">
                      Answer Post Generator
                    </h3>
                    <p className="text-sm text-gray-600">
                      Generate a publish-ready answer post (1,600–2,300 words) with SEO structure, FAQs, internal links, and a custom CTA.
                    </p>
                    <p className="text-sm text-orange-600 font-medium mt-1">
                      ✓ Ready to generate from any question
                    </p>
                  </div>
                </div>

                {/* CTA Button Area */}
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all bg-orange-600 text-white group-hover:bg-orange-700 group-hover:gap-3">
                    Generate Answer Post
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </div>
                  <span className="text-xs text-gray-500">Uses 1 post credit</span>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Automation Tools Section */}
        <div className="mt-12 mb-6">
          <div className="inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
            🚀 AUTOMATION & SCALING
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Content at Scale</h2>
          <p className="text-gray-600">Manage your content pipeline and publish automatically</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            className="bg-white rounded-2xl p-6 border-2 border-gray-200 text-left relative overflow-hidden opacity-60"
          >
            <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
              Coming Soon
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-gray-900">Content Planner</h3>
            <p className="text-gray-600 text-sm">Map out your next 30 questions before publishing</p>
          </div>

          <button
            onClick={() => navigate('/content-library')}
            className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-green-500 hover:shadow-xl transition-all text-left group"
          >
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-green-600 transition-colors">Content Library</h3>
            <p className="text-gray-600 text-sm">Edit, refine, and manage every generated post in one place</p>
          </button>

          <button
            onClick={() => navigate('/integrations')}
            className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-purple-500 hover:shadow-xl transition-all text-left group"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <LinkIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-purple-600 transition-colors">Integrations</h3>
            <p className="text-gray-600 text-sm">One-click publish to WordPress, Duda, Wix (Pro)</p>
          </button>
        </div>

        {/* Upgrade CTA */}
        {user.plan === 'free' && (
          <div className="mt-12 bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white" data-testid="upgrade-cta">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Upgrade to Pro</h3>
                <p className="text-orange-50">Unlock unlimited campaigns, batch images, and more. Only $27/mo (Founder Price)</p>
              </div>
              <Button 
                className="bg-white text-orange-600 hover:bg-orange-50 font-semibold px-8"
                data-testid="upgrade-btn"
              >
                Upgrade Now
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;